import {
  Download as DownloadIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Box, Button, Card, Chip, LinearProgress, Skeleton, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useDirection } from '../../context/DirectionContext';
import { translate as tr } from '../../i18n/translations';
import apiService from '../../services/api';
import { FormTrackerEntryDto } from '../../types/api';
import { buildMissingSubmissionsText, downloadText } from '../../utils/submissionTrackerExport';

export interface SubmissionTrackerCardProps {
  cycleId: string;
}

const FormRow: React.FC<{ form: FormTrackerEntryDto; expanded: boolean; onToggle: () => void }> = ({
  form,
  expanded,
  onToggle,
}) => {
  const { lang } = useDirection();
  const pct = form.expectedCount > 0 ? (form.submittedCount / form.expectedCount) * 100 : 100;
  const hasMissing = form.missingBySubmitter.length > 0;

  return (
    <Box sx={{ borderBottom: '1px solid #F1F5F9' }}>
      <Box
        onClick={hasMissing ? onToggle : undefined}
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          cursor: hasMissing ? 'pointer' : 'default',
          '&:hover': hasMissing ? { bgcolor: '#F8FAFC' } : undefined,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#0F172A' }}>
              {form.formName}
            </Typography>
            <Chip label={form.formType} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#F1F5F9' }} />
          </Box>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 5,
              borderRadius: 3,
              bgcolor: '#F1F5F9',
              '& .MuiLinearProgress-bar': { bgcolor: hasMissing ? '#D97706' : '#16A34A', borderRadius: 3 },
            }}
          />
        </Box>
        <Typography sx={{ fontSize: '0.78rem', color: '#64748B', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          {form.submittedCount}/{form.expectedCount} {tr('dash.submitted', lang)}
        </Typography>
        {hasMissing && (expanded ? <ExpandLessIcon sx={{ color: '#94A3B8' }} /> : <ExpandMoreIcon sx={{ color: '#94A3B8' }} />)}
      </Box>

      {expanded && hasMissing && (
        <Box sx={{ px: 3, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {form.missingBySubmitter.map(submitter => (
            <Box key={submitter.userId} sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#B45309', flexShrink: 0 }}>
                {tr('dash.missingFrom', lang)} {submitter.userName}:
              </Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#64748B' }}>
                {submitter.missingCandidates.map(c => `#${c.candidateNumber}`).join(', ')}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export const SubmissionTrackerCard: React.FC<SubmissionTrackerCardProps> = ({ cycleId }) => {
  const { lang } = useDirection();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const { data: tracker, isLoading } = useQuery({
    queryKey: ['submissionTracker', cycleId],
    queryFn: () => apiService.getSubmissionTracker(cycleId),
    enabled: !!cycleId,
  });

  const toggle = (id: string) =>
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const totalMissing = tracker?.forms.reduce((sum, f) => sum + f.missingBySubmitter.length, 0) ?? 0;

  const handleExport = () => {
    if (!tracker) return;
    const text = buildMissingSubmissionsText(tracker);
    const safeCycleName = tracker.cycleDisplayName.replace(/[\s/\\:*?"<>|]+/g, '_');
    downloadText(text, `missing-submissions-${safeCycleName}.txt`);
  };

  return (
    <Card sx={{ mb: 3 }}>
      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: '1px solid #F1F5F9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{tr('dash.submissionTracker', lang)}</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', mt: 0.25 }}>
            {tr('dash.submissionTrackerSub', lang)}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownloadIcon sx={{ fontSize: 15 }} />}
          onClick={handleExport}
          disabled={!tracker || totalMissing === 0}
          sx={{ flexShrink: 0, fontSize: '0.78rem' }}
        >
          {tr('dash.exportMissing', lang)}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ p: 3 }}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} height={44} sx={{ mb: 1, borderRadius: 2 }} />
          ))}
        </Box>
      ) : !tracker || tracker.forms.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: '#94A3B8', fontSize: '0.875rem' }}>{tr('dash.noTrackedForms', lang)}</Typography>
        </Box>
      ) : totalMissing === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: '#16A34A', fontSize: '0.875rem', fontWeight: 600 }}>
            {tr('dash.allSubmitted', lang)}
          </Typography>
        </Box>
      ) : (
        tracker.forms.map(form => (
          <FormRow
            key={form.cycleFormAssignmentId}
            form={form}
            expanded={expandedIds.has(form.cycleFormAssignmentId)}
            onToggle={() => toggle(form.cycleFormAssignmentId)}
          />
        ))
      )}
    </Card>
  );
};

export default SubmissionTrackerCard;
